import React, { useEffect, useState } from 'react';
import { Layout, theme, Row, Col, Card, Statistic, Typography, Space, Badge, Spin, List, Avatar, Tag } from 'antd';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    TeamOutlined,
    TrophyOutlined,
    ShoppingOutlined,
    CalendarOutlined,
    UserOutlined,
    ClockCircleOutlined,
    ManOutlined,
    WomanOutlined
} from '@ant-design/icons';
import { APPBACKGROUND_COLOR } from '../appsettings.js';
import axiosClient from '../../api/axiosClient';

const { Content } = Layout;
const { Title, Text } = Typography;

// Theme colors matching the system
const THEME_COLORS = {
    primary: '#209c96',
    secondary: '#1a1a2e',
    accent1: '#eb2f96',
    accent2: '#722ed1',
    accent3: '#fa8c16',
    background: APPBACKGROUND_COLOR
};

function Dashboard() {
    const USER_DNAME = localStorage.getItem("USERDPNAME");
    const USER_ROLE = localStorage.getItem("USERDESIGNATION");
    const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

    const [currentDate, setCurrentDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalMembers: 0,
        activeMembers: 0,
        totalTrainers: 0,
        activeTrainers: 0,
        totalSalesItems: 0,
        activeSalesItems: 0,
        recentMembers: [],
        recentTrainers: []
    });

    useEffect(() => {
        const now = new Date();
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        setCurrentDate(now.toLocaleDateString('en-US', options));

        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            const response = await axiosClient.get('/dashboard/get_dashboard_stats.php');
            if (response.data.status === 'success') {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    // Stat card style
    const cardStyle = {
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%'
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getGenderIcon = (gender) => {
        if (gender?.toLowerCase() === 'male') {
            return <ManOutlined style={{ color: '#1890ff' }} />;
        } else if (gender?.toLowerCase() === 'female') {
            return <WomanOutlined style={{ color: '#eb2f96' }} />;
        }
        return <UserOutlined />;
    };

    return (
        <Layout style={{ backgroundColor: THEME_COLORS.background }}>
            <Headerdiv />
            <Content style={{ padding: '16px' }}>
                <Spin spinning={loading} size="large">
                    <div style={{ 
                        padding: '24px', 
                        minHeight: '75vh', 
                        background: colorBgContainer, 
                        borderRadius: borderRadiusLG 
                    }}>
                        
                        {/* Welcome Header */}
                        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                            <Col xs={24} md={16}>
                                <Space direction="vertical" size={4}>
                                    <Title level={2} style={{ margin: 0, color: THEME_COLORS.secondary }}>
                                        Welcome back, {USER_DNAME || 'User'}
                                    </Title>
                                    <Space size="middle">
                                        <Badge 
                                            count={USER_ROLE || 'Staff'} 
                                            style={{ 
                                                backgroundColor: THEME_COLORS.primary,
                                                fontSize: '12px',
                                                padding: '0 12px'
                                            }} 
                                        />
                                        <Text type="secondary">
                                            <CalendarOutlined style={{ marginRight: 8 }} />
                                            {currentDate}
                                        </Text>
                                    </Space>
                                </Space>
                            </Col>
                        </Row>

                        {/* Stats Cards */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} lg={6}>
                                <Card style={{ ...cardStyle, borderLeft: `4px solid ${THEME_COLORS.primary}` }}>
                                    <Statistic
                                        title={<Text strong>Total Members</Text>}
                                        value={stats.totalMembers}
                                        prefix={<TeamOutlined style={{ color: THEME_COLORS.primary }} />}
                                        valueStyle={{ color: THEME_COLORS.primary }}
                                        suffix={<Text type="secondary" style={{ fontSize: '14px' }}>/ {stats.activeMembers} active</Text>}
                                    />
                                </Card>
                            </Col>
                            
                            <Col xs={24} sm={12} lg={6}>
                                <Card style={{ ...cardStyle, borderLeft: `4px solid ${THEME_COLORS.accent1}` }}>
                                    <Statistic
                                        title={<Text strong>Active Trainers</Text>}
                                        value={stats.activeTrainers}
                                        prefix={<TrophyOutlined style={{ color: THEME_COLORS.accent1 }} />}
                                        valueStyle={{ color: THEME_COLORS.accent1 }}
                                        suffix={<Text type="secondary" style={{ fontSize: '14px' }}>/ {stats.totalTrainers} total</Text>}
                                    />
                                </Card>
                            </Col>
                            
                            <Col xs={24} sm={12} lg={6}>
                                <Card style={{ ...cardStyle, borderLeft: `4px solid ${THEME_COLORS.accent2}` }}>
                                    <Statistic
                                        title={<Text strong>Sales Items</Text>}
                                        value={stats.totalSalesItems}
                                        prefix={<ShoppingOutlined style={{ color: THEME_COLORS.accent2 }} />}
                                        valueStyle={{ color: THEME_COLORS.accent2 }}
                                        suffix={<Text type="secondary" style={{ fontSize: '14px' }}>/ {stats.activeSalesItems} active</Text>}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} lg={6}>
                                <Card style={{ ...cardStyle, borderLeft: `4px solid ${THEME_COLORS.accent3}` }}>
                                    <Statistic
                                        title={<Text strong>Active Assignments</Text>}
                                        value={stats.totalAssignments || 0}
                                        prefix={<TeamOutlined style={{ color: THEME_COLORS.accent3 }} />}
                                        valueStyle={{ color: THEME_COLORS.accent3 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Quick Info Section */}
                        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                            <Col xs={24} lg={8}>
                                <Card 
                                    title={
                                        <Space>
                                            <TeamOutlined style={{ color: THEME_COLORS.primary }} />
                                            <span>Recent Members</span>
                                        </Space>
                                    }
                                    style={cardStyle}
                                    bodyStyle={{ padding: stats.recentMembers?.length === 0 ? '24px' : '0' }}
                                >
                                    {stats.recentMembers?.length > 0 ? (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={stats.recentMembers}
                                            renderItem={(member) => (
                                                <List.Item style={{ padding: '12px 24px' }}>
                                                    <List.Item.Meta
                                                        avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: THEME_COLORS.primary }} />}
                                                        title={
                                                            <Space>
                                                                <span>{member.name}</span>
                                                                {getGenderIcon(member.gender)}
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space>
                                                                <Tag color="cyan">{member.memberId}</Tag>
                                                                <Text type="secondary">
                                                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                                    {formatDate(member.registeredDate)}
                                                                </Text>
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    ) : (
                                        <Text type="secondary">No recent members to display</Text>
                                    )}
                                </Card>
                            </Col>
                            
                            <Col xs={24} lg={8}>
                                <Card 
                                    title={
                                        <Space>
                                            <TrophyOutlined style={{ color: THEME_COLORS.accent1 }} />
                                            <span>Recent Trainers</span>
                                        </Space>
                                    }
                                    style={cardStyle}
                                    bodyStyle={{ padding: stats.recentTrainers?.length === 0 ? '24px' : '0' }}
                                >
                                    {stats.recentTrainers?.length > 0 ? (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={stats.recentTrainers}
                                            renderItem={(trainer) => (
                                                <List.Item style={{ padding: '12px 24px' }}>
                                                    <List.Item.Meta
                                                        avatar={<Avatar icon={<TrophyOutlined />} style={{ backgroundColor: THEME_COLORS.accent1 }} />}
                                                        title={
                                                            <Space>
                                                                <span>{trainer.name}</span>
                                                                {getGenderIcon(trainer.gender)}
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space>
                                                                <Tag color="magenta">{trainer.trainerId}</Tag>
                                                                <Text type="secondary">
                                                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                                    {formatDate(trainer.registeredDate)}
                                                                </Text>
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    ) : (
                                        <Text type="secondary">No recent trainers to display</Text>
                                    )}
                                </Card>
                            </Col>

                            <Col xs={24} lg={8}>
                                <Card 
                                    title={
                                        <Space>
                                            <TrophyOutlined style={{ color: THEME_COLORS.accent3 }} />
                                            <span>Top Trainers (Workload)</span>
                                        </Space>
                                    }
                                    style={cardStyle}
                                    bodyStyle={{ padding: stats.topTrainers?.length === 0 ? '24px' : '0' }}
                                >
                                    {stats.topTrainers?.length > 0 ? (
                                        <List
                                            itemLayout="horizontal"
                                            dataSource={stats.topTrainers}
                                            renderItem={(trainer) => (
                                                <List.Item style={{ padding: '12px 24px' }}>
                                                    <List.Item.Meta
                                                        avatar={<Avatar style={{ backgroundColor: THEME_COLORS.accent3 }}>{trainer.count}</Avatar>}
                                                        title={<span>{trainer.name}</span>}
                                                        description={
                                                            <Tag color="orange"> {trainer.count} Members Assigned</Tag>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    ) : (
                                        <Text type="secondary">No data available</Text>
                                    )}
                                </Card>
                            </Col>
                        </Row>

                    </div>
                </Spin>
            </Content>
            <Footerdiv />
        </Layout>
    );
}

export default Dashboard;